import {
	CalendarIcon,
	ChatIcon,
	ExpenseIcon,
	ExploreIcon,
	GroupsIcon,
	IdeaIcon,
	MoreHorizIcon,
	SearchIcon,
} from '@/assets/icons'
import Modal from '@components/Modal'
import { backgroundOrange } from '@/theme/colors'
import { trackEvent } from '@lib/analytics'
import type { SvgIconComponent } from '@mui/icons-material'
import { DiscoverySourceType } from '@orbit/api-types'
import {
	DISCOVERY_OPTIONS,
	INTENT_OPTIONS,
	SurveyTransition,
	advance,
	canAdvance,
	clearSurveyPending,
	ctaLabel,
	initialSurveyState,
	selectIntent,
	selectSource,
	skip,
	stepTitle,
} from '@lib/onboardingSurvey'
import React, { useState } from 'react'
import { ButtonGroupComponent } from '../../pages/Itinerary/AddEventModal/FormComponents'
import {
	OptionButton,
	OptionDescription,
	OptionIconChip,
	OptionList,
	OptionTexts,
	OptionTitle,
	SurveyCta,
} from './OnboardingSurveyModal.styles'
import { iconSizes } from '@/theme/tokens'

type SurveyIcon = SvgIconComponent

// Icons are a UI concern (the survey lib stays pure). Glyphs mirror the mobile
// apps' Material icons: calendar / lightbulb / card / compass on intent,
// people / chat / search / ellipsis on discovery.
const INTENT_ICONS: Record<string, SurveyIcon> = {
	itinerary: CalendarIcon,
	ideas: IdeaIcon,
	expenses: ExpenseIcon,
	exploring: ExploreIcon,
}

// Total over the enum so a new discovery source can't ship without an icon
// decision; AppStoreSearch never renders on web (its option is omitted).
const DISCOVERY_ICONS: Record<DiscoverySourceType, SurveyIcon> = {
	FriendOrFamily: GroupsIcon,
	SocialMedia: ChatIcon,
	AppStoreSearch: SearchIcon,
	WebSearch: SearchIcon,
	Other: MoreHorizIcon,
}

interface OnboardingSurveyModalProps {
	isOpen: boolean
	onClose: () => void
}

const OnboardingSurveyModal: React.FC<OnboardingSurveyModalProps> = ({ isOpen, onClose }) => {
	const [state, setState] = useState(initialSurveyState)

	const applyTransition = (transition: SurveyTransition) => {
		transition.events.forEach(event => trackEvent(event.eventType, event.data))
		if (transition.done) {
			clearSurveyPending()
			onClose()
		} else {
			setState(transition.state)
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={() => applyTransition(skip(state))}
			title={stepTitle(state.step)}
			size="medium"
			backgroundColor={backgroundOrange}
			footer={
				<ButtonGroupComponent>
					<SurveyCta
						type="button"
						variant="primary"
						disabled={!canAdvance(state)}
						onClick={() => applyTransition(advance(state))}
					>
						{ctaLabel(state.step)}
					</SurveyCta>
				</ButtonGroupComponent>
			}
		>
			{state.step === 'intent' ? (
				<OptionList>
					{INTENT_OPTIONS.map((option, index) => {
						const Icon = INTENT_ICONS[option.id]
						const selected = state.selectedIntents.includes(option.id)
						return (
							<OptionButton
								key={option.id}
								type="button"
								$selected={selected}
								$index={index}
								aria-pressed={selected}
								onClick={() => setState(current => selectIntent(current, option.id))}
							>
								{Icon && (
									<OptionIconChip $selected={selected} aria-hidden="true">
										<Icon sx={{ fontSize: iconSizes.lg }} />
									</OptionIconChip>
								)}
								<OptionTexts>
									<OptionTitle>{option.title}</OptionTitle>
									<OptionDescription>{option.description}</OptionDescription>
								</OptionTexts>
							</OptionButton>
						)
					})}
				</OptionList>
			) : (
				<OptionList>
					{DISCOVERY_OPTIONS.map((option, index) => {
						const Icon = DISCOVERY_ICONS[option.value]
						const selected = state.selectedSource === option.value
						return (
							<OptionButton
								key={option.value}
								type="button"
								$selected={selected}
								$index={index}
								aria-pressed={selected}
								onClick={() => setState(current => selectSource(current, option.value))}
							>
								<OptionIconChip $selected={selected} aria-hidden="true">
									<Icon sx={{ fontSize: iconSizes.lg }} />
								</OptionIconChip>
								<OptionTexts>
									<OptionTitle>{option.label}</OptionTitle>
								</OptionTexts>
							</OptionButton>
						)
					})}
				</OptionList>
			)}
		</Modal>
	)
}

export default OnboardingSurveyModal
